import React, { useMemo, useState } from 'react';
import { Bug, Search, Shield, Save, X, UserPlus } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { useAuth } from '../components/AuthProvider';
import { useApiJson } from '../hooks/useApi';
import { cn } from '../lib/utils';
import { apiUrl } from '../lib/api-base';
import { captureSentryTestError } from '../lib/sentry';
import type { FeatureAllowRole, FeatureKey, FeaturePolicy, Member } from '../types';
import {
  FEATURE_ALLOW_ROLES,
  FEATURE_DESCRIPTIONS,
  FEATURE_LABELS,
  featureAccessSummary,
} from '../lib/feature-access';
import { updateFeaturePolicy } from '../lib/feature-access-api';
import { FEATURE_KEYS } from '../types';

interface MembersDto {
  ok: boolean;
  members: Member[];
}

interface FeatureAccessPolicyDto {
  ok: boolean;
  policy: FeaturePolicy;
}

const ROLE_LABELS: Record<FeatureAllowRole, string> = {
  admin: 'Admin',
  member: 'Member',
};

function FeatureCard({
  feature,
  policy,
  members,
  onChange,
}: {
  feature: FeatureKey;
  policy: FeaturePolicy;
  members: Member[];
  onChange: (next: FeaturePolicy) => void;
}) {
  const rule = policy[feature];
  const [search, setSearch] = useState('');

  const filteredMembers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return members.slice(0, 8);
    return members
      .filter(
        (m) =>
          m.name.toLowerCase().includes(q) ||
          m.email.toLowerCase().includes(q) ||
          (m.lmNumber ?? '').toLowerCase().includes(q),
      )
      .slice(0, 8);
  }, [members, search]);

  const updateRule = (patch: Partial<typeof rule>) => {
    onChange({
      ...policy,
      [feature]: { ...rule, ...patch },
    });
  };

  const toggleRole = (role: FeatureAllowRole) => {
    const allowedRoles = rule.allowedRoles.includes(role)
      ? rule.allowedRoles.filter((r) => r !== role)
      : [...rule.allowedRoles, role];
    updateRule({ allowedRoles });
  };

  const addEmail = (email: string) => {
    const normalized = email.trim().toLowerCase();
    if (!normalized || rule.allowedEmails.includes(normalized)) return;
    updateRule({ allowedEmails: [...rule.allowedEmails, normalized] });
  };

  const removeEmail = (email: string) => {
    updateRule({ allowedEmails: rule.allowedEmails.filter((e) => e !== email) });
  };

  const allowlistedMembers = members.filter((m) =>
    rule.allowedEmails.includes(m.email.trim().toLowerCase()),
  );

  return (
    <section className="bg-[var(--color-surface-1)] rounded-[var(--radius-lg)] border border-[var(--color-border)] p-6 space-y-5">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">
            {FEATURE_LABELS[feature]}
          </h2>
          <p className="text-sm text-[var(--color-text-secondary)] mt-1">
            {FEATURE_DESCRIPTIONS[feature]}
          </p>
        </div>
        <label className="inline-flex items-center gap-3 cursor-pointer">
          <span className="text-sm font-medium text-[var(--color-text-secondary)]">Enabled</span>
          <input
            type="checkbox"
            checked={rule.enabled}
            onChange={(e) => updateRule({ enabled: e.target.checked })}
            className="h-5 w-5 rounded border-[var(--color-border)] accent-[var(--color-accent)]"
          />
        </label>
      </div>

      <p className="text-xs text-[var(--color-text-tertiary)]">{featureAccessSummary(rule)}</p>

      {!rule.enabled && (
        <div className="space-y-4 pt-2 border-t border-[var(--color-border)]">
          <div>
            <p className="text-sm font-medium text-[var(--color-text-primary)] mb-2">
              Role bypass
            </p>
            <div className="flex flex-wrap gap-3">
              {FEATURE_ALLOW_ROLES.map((role) => (
                <label key={role} className="inline-flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rule.allowedRoles.includes(role)}
                    onChange={() => toggleRole(role)}
                    className="rounded border-[var(--color-border)] accent-[var(--color-accent)]"
                  />
                  {ROLE_LABELS[role]}
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <p className="text-sm font-medium text-[var(--color-text-primary)]">
              Member allowlist
            </p>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-text-tertiary)]" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search members by name or email"
                className="w-full pl-10 pr-4 py-2.5 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface-0)] text-sm"
              />
            </div>

            {filteredMembers.length > 0 && (
              <ul className="border border-[var(--color-border)] rounded-[var(--radius-md)] divide-y divide-[var(--color-border)]">
                {filteredMembers.map((member) => {
                  const normalized = member.email.trim().toLowerCase();
                  const selected = rule.allowedEmails.includes(normalized);
                  return (
                    <li key={member.uid}>
                      <button
                        type="button"
                        onClick={() => (selected ? removeEmail(normalized) : addEmail(member.email))}
                        className={cn(
                          'w-full flex items-center justify-between gap-3 px-4 py-3 text-left text-sm transition-colors',
                          selected
                            ? 'bg-[var(--color-accent-subtle)] text-[var(--color-accent)]'
                            : 'hover:bg-[var(--color-surface-2)]',
                        )}
                      >
                        <span>
                          <span className="font-medium">{member.name}</span>
                          <span className="block text-xs text-[var(--color-text-tertiary)]">
                            {member.email}
                          </span>
                        </span>
                        {selected ? (
                          <X className="w-4 h-4 shrink-0" />
                        ) : (
                          <UserPlus className="w-4 h-4 shrink-0 text-[var(--color-text-tertiary)]" />
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            {allowlistedMembers.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {allowlistedMembers.map((member) => (
                  <button
                    key={member.uid}
                    type="button"
                    onClick={() => removeEmail(member.email.trim().toLowerCase())}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-[var(--color-surface-2)] text-[var(--color-text-secondary)] hover:bg-[var(--color-error-subtle)] hover:text-[var(--color-error)]"
                  >
                    {member.name}
                    <X className="w-3 h-3" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

export function SuperAdminPage() {
  const { refresh } = useAuth();
  const { data, loading, mutate } = useApiJson<FeatureAccessPolicyDto>(
    '/api/settings/feature-access',
  );
  const { data: membersData } = useApiJson<MembersDto>('/api/members');
  const [draft, setDraft] = useState<FeaturePolicy | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [sentryStatus, setSentryStatus] = useState<string | null>(null);
  const [sentryBusy, setSentryBusy] = useState<'spa' | 'worker' | null>(null);

  const policy = draft ?? data?.policy ?? null;
  const members = membersData?.members ?? [];

  const handleSave = async () => {
    if (!policy) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const next = await updateFeaturePolicy(policy);
      setDraft(next);
      await mutate();
      await refresh();
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save feature policy');
    } finally {
      setSaving(false);
    }
  };

  const sendSpaSentryTest = async () => {
    setSentryBusy('spa');
    setSentryStatus(null);
    try {
      const eventId = await captureSentryTestError('super-admin');
      setSentryStatus(
        eventId
          ? `SPA test sent (event ${eventId}). Check the smym-app project in Sentry.`
          : 'SPA test fired but Sentry returned no event ID — check the browser network tab.',
      );
    } catch (e) {
      setSentryStatus(e instanceof Error ? e.message : 'SPA test failed');
    } finally {
      setSentryBusy(null);
    }
  };

  const sendWorkerSentryTest = async () => {
    setSentryBusy('worker');
    setSentryStatus(null);
    try {
      const res = await fetch(apiUrl('/api/debug-sentry'), { credentials: 'include' });
      if (res.ok) {
        setSentryStatus('Worker returned success unexpectedly — check Sentry anyway.');
      } else {
        setSentryStatus('Worker test error triggered (HTTP 500). Check the SMYM API project in Sentry.');
      }
    } catch (e) {
      setSentryStatus(e instanceof Error ? e.message : 'Worker test request failed');
    } finally {
      setSentryBusy(null);
    }
  };

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl">
        <header className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 text-[var(--color-accent)] mb-2">
              <Shield className="w-4 h-4" />
              <span className="text-xs font-semibold uppercase tracking-wide">Super Admin</span>
            </div>
            <h1 className="text-2xl font-semibold text-[var(--color-text-primary)]">
              Feature access
            </h1>
            <p className="text-[var(--color-text-secondary)] text-sm mt-1">
              Control which app areas are available globally, by role, or for specific members.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={!policy || saving}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-[var(--radius-md)] bg-[var(--color-accent)] text-white text-sm font-medium disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </header>

        {error && (
          <p className="text-sm text-[var(--color-error)] bg-[var(--color-error-subtle)] rounded-[var(--radius-md)] px-4 py-3">
            {error}
          </p>
        )}
        {saved && (
          <p className="text-sm text-[var(--color-success-text)] bg-[var(--color-success-subtle)] rounded-[var(--radius-md)] px-4 py-3">
            Feature policy saved.
          </p>
        )}

        {loading || !policy ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-40 rounded-[var(--radius-lg)] bg-[var(--color-surface-2)] animate-pulse"
              />
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {FEATURE_KEYS.map((feature) => (
              <div key={feature}>
                <FeatureCard
                  feature={feature}
                  policy={policy}
                  members={members}
                  onChange={setDraft}
                />
              </div>
            ))}
          </div>
        )}

        <section className="bg-[var(--color-surface-1)] rounded-[var(--radius-lg)] border border-[var(--color-border)] p-6 space-y-4">
          <div className="inline-flex items-center gap-2 text-[var(--color-text-secondary)]">
            <Bug className="w-4 h-4" />
            <span className="text-xs font-semibold uppercase tracking-wide">Diagnostics</span>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">Sentry verification</h2>
            <p className="text-sm text-[var(--color-text-secondary)] mt-1">
              Send test errors to confirm production error tracking. Safe for super-admins only.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => void sendSpaSentryTest()}
              disabled={sentryBusy !== null}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface-0)] text-sm font-medium disabled:opacity-50"
            >
              {sentryBusy === 'spa' ? 'Sending…' : 'Test SPA error'}
            </button>
            <button
              type="button"
              onClick={() => void sendWorkerSentryTest()}
              disabled={sentryBusy !== null}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface-0)] text-sm font-medium disabled:opacity-50"
            >
              {sentryBusy === 'worker' ? 'Sending…' : 'Test Worker error'}
            </button>
          </div>
          {sentryStatus && (
            <p className="text-sm text-[var(--color-text-secondary)] bg-[var(--color-surface-2)] rounded-[var(--radius-md)] px-4 py-3">
              {sentryStatus}
            </p>
          )}
        </section>
      </div>
    </Layout>
  );
}
